import { AppointmentTooltip as DXAppointmentTooltip } from "@devexpress/dx-react-scheduler-material-ui";
import * as React from "react";

import AddIcon from "@mui/icons-material/Add";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import Lens from "@mui/icons-material/Lens";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import classNames from "clsx";
import { format } from "date-fns";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { getRRuleText } from "components/recurrence-rule-editor/utils";
import {
  RECURRENCE_DAYS,
  RECURRENCE_WEEKS,
  REOCCURRENCE_PERIODS,
  REOCCURRENCE_PERIOD_TYPE_MONTHLY,
  REOCCURRENCE_PERIOD_TYPE_WEEKLY
} from "constants/appointments";
import { NON_BREAKABLE_SPACE } from "constants/common";
import {
  selectCurrentPractice,
  selectNumberOfPracticeClinics
} from "features/auth/auth-slice";
import useAppointments from "features/scheduling/hooks/use-appointments.hook";
import usePatientCreateAppointmentMode from "features/scheduling/patient-create-appointment-mode-modal/use-patient-create-appointment-mode.hook";
import { hidePreview } from "features/scheduling/scheduler-slice";
import { AppointmentBlock } from "services/types/events.type";
import { formatBackendDateOnly } from "utils/common";

import PreviewContentRow from "./appointment-preview-content/preview-content-row.component";
import { classes } from "./appointment-preview.component";

type AppointmentBlockPreviewProps = {
  appointmentData: DXAppointmentTooltip.ContentProps["appointmentData"] &
    AppointmentBlock;
  formatDate: DXAppointmentTooltip.ContentProps["formatDate"];
};
export default function AppointmentBlockPreview({
  appointmentData,
  formatDate
}: AppointmentBlockPreviewProps) {
  const dispatch = useAppDispatch();
  const numberOfPracticeClinics =
    useAppSelector(selectNumberOfPracticeClinics) || 0;
  const currentPractice = useAppSelector(selectCurrentPractice);
  const isCrossClinicConflictGateOn = Boolean(
    currentPractice?.scheduling_cross_clinic_conflict_check
  );
  const multiClinic = numberOfPracticeClinics > 1;
  const {
    appointmentType,
    startDate,
    endDate,
    staffMember,
    staffMemberId,
    endRecurrenceDate,
    recurrencePeriod,
    recurrenceDay,
    recurrenceWeek,
    clinic,
    title,
    notes
  } = appointmentData;

  const isEveryDayInAWeekOfTheMonth =
    recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_MONTHLY &&
    Boolean(recurrenceWeek);

  const { setEditingForm } = useAppointments();

  const { setupPatientCreateAppointmentModeModal } =
    usePatientCreateAppointmentMode();

  const rruleText = getRRuleText(
    appointmentData.rrule || appointmentData.schedule?.rrule
  );

  return (
    <Grid container alignItems="flex-start" className={classes.titleContainer}>
      <Grid item xs={2} pt={0.75} className={classNames(classes.textCenter)}>
        <Lens className={classNames(classes.colorfulContent)} />
      </Grid>
      <Grid item xs={10}>
        <Stack>
          <Box
            className={classNames(classes.title, classes.dateAndTitle)}
            data-testid="appointment-block-preview-title"
            sx={{ pb: 0.7 }}
          >
            {`${
              title || appointmentType?.name || "Dedicated"
            } appointment slot for ${staffMember.fullName.replace(
              ". ",
              `.${NON_BREAKABLE_SPACE}`
            )}`}
          </Box>
          <Typography
            className={classNames(classes.text, classes.dateAndTitle)}
          >
            {format(new Date(startDate), "EEEE, LLLL do")} /{" "}
            {`${formatDate(startDate, {
              hour: "numeric",
              minute: "numeric"
            })}
              - ${formatDate(endDate, {
                hour: "numeric",
                minute: "numeric"
              })}`}
          </Typography>
          {rruleText ? (
            <Typography>{rruleText}</Typography>
          ) : (
            <>
              {recurrencePeriod && !isEveryDayInAWeekOfTheMonth && (
                <Typography>
                  {REOCCURRENCE_PERIODS.getFromCode(recurrencePeriod)} on{" "}
                  {recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_WEEKLY
                    ? RECURRENCE_DAYS.getFromCode(recurrenceDay)
                    : `the ${format(new Date(startDate), "do")}`}
                  {Boolean(endRecurrenceDate) && endRecurrenceDate !== null && (
                    <>
                      {" "}
                      until{" "}
                      {formatBackendDateOnly(
                        String(endRecurrenceDate),
                        "EEE MMM dd"
                      )}
                    </>
                  )}
                </Typography>
              )}
              {recurrencePeriod && isEveryDayInAWeekOfTheMonth && (
                <Typography>
                  {`${RECURRENCE_DAYS.getFromCode(
                    recurrenceDay
                  )}, ${RECURRENCE_WEEKS.getFromCode(recurrenceWeek)}`}
                  {Boolean(endRecurrenceDate) && endRecurrenceDate !== null && (
                    <>
                      {" "}
                      until{" "}
                      {formatBackendDateOnly(
                        String(endRecurrenceDate),
                        "EEE MMM dd"
                      )}
                    </>
                  )}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </Grid>
      {multiClinic && (
        <Grid item xs={12} marginTop={1}>
          <PreviewContentRow
            Icon={LocationOnOutlinedIcon}
            content={clinic.name}
            sx={{ mb: 2.5 }}
          />
        </Grid>
      )}

      {notes && (
        <PreviewContentRow
          Icon={ContentPasteOutlinedIcon}
          content={
            <Typography sx={{ whiteSpace: "pre-line" }}>{notes}</Typography>
          }
        />
      )}

      <Grid item xs={12} marginTop={1}>
        <PreviewContentRow
          content={
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                dispatch(hidePreview());
                setupPatientCreateAppointmentModeModal({
                  appointmentType,
                  clinic,
                  staffMemberId,
                  staffMember,
                  // ENG-1848. Booking a patient into a dedicated slot pre-authorized the
                  // overlap, so the provider conflict check never ran — a live bypass,
                  // and the block itself is a Schedule the check never looks at anyway.
                  // Gate on: drop the flag and let the form's ProviderConflictModal
                  // (ENG-1831) present a real conflict.
                  ...(isCrossClinicConflictGateOn
                    ? {}
                    : { allow_overlap: true }),
                  scheduled_block_id: appointmentData.modelId,
                  startDate,
                  endDate
                });
                setEditingForm(true);
              }}
            >
              New Appointment
            </Button>
          }
          sx={{ mb: 2.5 }}
        />
      </Grid>
    </Grid>
  );
}
